/**
 * Pure helpers for dates entered as separate year, month and day boxes. Everything is a plain
 * 'YYYY-MM-DD' string so nothing goes through a time zone (a picked 5 May is stored as 5 May).
 */

export interface DateParts {
  y: string;
  m: string;
  d: string;
}

export const EMPTY_PARTS: DateParts = { y: '', m: '', d: '' };

/** 'YYYY-MM-DD' (or a local Date) to the three box values; empty parts when it isn't a real date. */
export function toDateParts(value: string | Date | null | undefined): DateParts {
  if (!value) return EMPTY_PARTS;
  let y: number, m: number, d: number;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return EMPTY_PARTS;
    y = value.getFullYear(); m = value.getMonth() + 1; d = value.getDate();
  } else {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return EMPTY_PARTS;
    y = Number(match[1]); m = Number(match[2]); d = Number(match[3]);
  }
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(m, y)) return EMPTY_PARTS;
  return { y: String(y), m: String(m), d: String(d) };
}

/** Days in a month; 31 until the month is known, and February 29 until the year is known. */
export function daysInMonth(month: number, year?: number): number {
  if (!month) return 31;
  return new Date(year || 2000, month, 0).getDate();
}

/** Applies a change to one box and pulls the day back into range (31 → 30 when June is chosen). */
export function updateDateParts(parts: DateParts, patch: Partial<DateParts>): DateParts {
  const next = { ...parts, ...patch };
  const limit = daysInMonth(Number(next.m), Number(next.y));
  if (next.d && Number(next.d) > limit) next.d = String(limit);
  return next;
}

/** 'YYYY-MM-DD' once all three boxes are chosen, otherwise null. */
export function datePartsToYmd(parts: DateParts): string | null {
  if (!parts.y || !parts.m || !parts.d) return null;
  return `${parts.y.padStart(4, '0')}-${parts.m.padStart(2, '0')}-${parts.d.padStart(2, '0')}`;
}
